import { ref } from 'vue'
import TranslateServer from '~/services/TranslateServer'
import type { CancelEstimateResp } from '~/services/TranslateServer/types'
import { NetCode } from '~/types/const'

/** 后端业务码：任务阶段或金额已变化，需要按新数字二次确认 */
const CODE_STAGE_CHANGED = '20045'

/** 弹窗形态 */
export type CancelDialogVariant =
  | 'queue'            // 弹窗1：排队中，免费取消
  | 'parse'            // 弹窗2：解析中，按页收费
  | 'translate'        // 弹窗3：翻译中，已扣不退
  | 'shiftToParse'     // 弹窗4：确认瞬间跳到了解析
  | 'shiftToTranslate' // 弹窗5：确认瞬间跳到了翻译

export interface CancelDialogState {
  variant: CancelDialogVariant
  operateRecordId: number
  fileName: string
  /** 调用方自定义的定位键（上传区传 tmpFileName），composable 只透传不解释，用于回调里精确定位是哪条记录 */
  contextKey?: string
  stage: number
  pageCount: number | null
  charsPerPage: number | null
  shouldCostChars: number
  wholeCharsNum: number | null
  costCharsNum: number | null
  /** 20045 里“阶段没变、只有应扣字符数变了”的情形，弹窗据此换一条“费用已更新”提示条 */
  charsChanged: boolean
  submitting: boolean
}

function variantOfStage(stage: number, isShift: boolean): CancelDialogVariant {
  if (stage === 1) return 'queue'
  if (stage === 2) return isShift ? 'shiftToParse' : 'parse'
  return isShift ? 'shiftToTranslate' : 'translate'
}

/** 由后端返回的估算/确认数据构造弹窗状态，供两处复用 */
function buildState(
  data: CancelEstimateResp,
  operateRecordId: number,
  fileName: string,
  isShift: boolean,
  contextKey?: string,
): CancelDialogState {
  const stage = data.stage ?? 3 // 阶段可能为空：保守当翻译中处理
  return {
    variant: variantOfStage(stage, isShift),
    operateRecordId,
    fileName,
    contextKey,
    stage,
    pageCount: data.pageCount ?? null,
    charsPerPage: data.charsPerPage ?? null,
    shouldCostChars: data.shouldCostChars ?? 0,
    wholeCharsNum: data.wholeCharsNum ?? null,
    costCharsNum: data.costCharsNum ?? null,
    charsChanged: false,
    submitting: false,
  }
}

export function useCancelTranslate(options: {
  /**
   * 服务端已确认取消成功时触发（且仅此一种情况）。
   * 终态（不可取消）走 onFinalState、失败/异常走 onError，是否刷新列表由它们各自决定。
   */
  onCancelled: (state: CancelDialogState) => void
  /** 任务已终态时的提示 */
  onFinalState: (finalStatus: string) => void
  /** 通用错误提示 */
  onError: (msg: string) => void
}) {
  const dialogVisible = ref(false)
  const dialogState = ref<CancelDialogState | null>(null)
  /** 并发打开弹窗时的序号（每个组件实例独立）：只有最后一次点击的响应能落到弹窗上，先到的过期响应直接丢弃 */
  let openSeq = 0

  /** 点击「取消」按钮：实时查阶段，决定弹哪个弹窗 */
  async function openCancelDialog(operateRecordId: number, fileName: string, contextKey?: string) {
    const seq = ++openSeq
    try {
      const res = await TranslateServer.getRequestCancelTranslateEstimate({ operateRecordId })
      // 期间又点了别的文件：这次的响应已过期，丢弃，避免弹窗标错文件
      if (seq !== openSeq) return
      if (!res || res.code !== NetCode.SUCCESS || !res.data) {
        options.onError(res?.message ?? '')
        return
      }
      const { data } = res
      if (!data.cancelable) {
        // 任务已是终态：没有取消动作发生，不能当作取消成功
        options.onFinalState(data.finalStatus ?? '')
        return
      }
      dialogState.value = buildState(data, operateRecordId, fileName, false, contextKey)
      dialogVisible.value = true
    } catch (e: any) {
      if (seq !== openSeq) return
      // 仅兜网络层（HTTP reject），业务失败走上面的 code 分支
      options.onError(e?.message ?? '')
    }
  }

  /** 点击弹窗上的「确认取消」/「仍要取消」 */
  async function onConfirmCancel() {
    const state = dialogState.value
    if (!state || state.submitting) return
    // 与 openCancelDialog 共用序号：请求在途期间弹窗若被关闭/换成别的文件，响应回来后不再碰弹窗状态
    const seq = ++openSeq
    state.submitting = true
    try {
      const res = await TranslateServer.getRequestCancelTranslateConfirm({
        operateRecordId: state.operateRecordId,
        confirmedStage: state.stage,
        confirmedChars: state.shouldCostChars,
      })
      if (seq !== openSeq) {
        // 弹窗已不属于这次请求：结果仍要通知调用方（成功要标记记录），但绝不改 dialogVisible / dialogState
        if (res.code === NetCode.SUCCESS) options.onCancelled(state)
        else if (res.code !== CODE_STAGE_CHANGED) options.onError(res.message ?? '')
        return
      }
      if (res.code === CODE_STAGE_CHANGED && res.data) {
        // 阶段或金额已变：服务端没有扣任何费用，按新数字重新弹窗让用户再确认一次
        const next = res.data
        if (next.stage === null || next.stage === undefined) {
          dialogVisible.value = false
          dialogState.value = null
          options.onError(res.message ?? '')
          return
        }
        // 后端两条路径都抛 20045：阶段跳变，或阶段没变但 shouldCostChars 变了，提示条文案不同
        const stageChanged = next.stage !== state.stage
        const nextState = buildState(next, state.operateRecordId, state.fileName, stageChanged, state.contextKey)
        nextState.charsChanged = !stageChanged
        dialogState.value = nextState
        return
      }
      if (res.code !== NetCode.SUCCESS) {
        // 业务失败（含 20046 等）：没有取消成功，只报错，是否刷新由调用方在 onError 里决定
        dialogVisible.value = false
        dialogState.value = null
        options.onError(res.message ?? '')
        return
      }
      dialogVisible.value = false
      dialogState.value = null
      // 服务端已确认取消成功，把当时的 state 传出去供调用方定位记录
      options.onCancelled(state)
    } catch (e: any) {
      // 仅兜网络层（HTTP reject）：取消结果未知，不能当作成功
      if (seq === openSeq) {
        dialogVisible.value = false
        dialogState.value = null
      }
      options.onError(e?.message ?? '')
    } finally {
      // 防止二次确认按钮卡死：只复位本次请求对应的 state（20045 路径已换成新对象，不需要）
      if (dialogState.value === state) {
        state.submitting = false
      }
    }
  }

  /** 点击「继续翻译」，或按 ESC / 点遮罩 —— 三者行为完全一致 */
  function onKeepTranslating() {
    dialogVisible.value = false
    dialogState.value = null
  }

  return { dialogVisible, dialogState, openCancelDialog, onConfirmCancel, onKeepTranslating }
}
